import {v4 as uuidv4} from "uuid";
import {div, removeClassByPrefix, isIframed} from '@megaphonenpm/mputils/src/DOM.js';
import {DebugPanel} from '@megaphonenpm/mpui/src/DebugPanel.js';
import {MegaScreen} from "./MegaScreen.js";
import {MediaView} from "./MediaView.js";
import {
  CHANGE, CLOSE, COMPLETE, OPEN, START, TASK_END
} from '@megaphonenpm/mputils/src/constants.js';
import {notNil, isNil, cascadingGetIfNotEmpty, notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {
  CLIENT_TYPE_SCREEN,
  MULTICHOICE_PRODUCT,
  SOCIAL_PRODUCT, STATE_CLOSED, STATE_SCENE, STATE_SCENE_RESULT,
  TITLE_PRODUCT
} from '@megaphonenpm/mrtsclient/src/constants.js';
import {get} from 'lodash-es';
import {RundownComponent} from './components/RundownComponent.js';
import {ElementTooltip} from './components/ElementTooltip.js';
import {ProgressBar} from './components/ProgressBar.js';
import {PostMessages} from './PostMessages.js';
import {RunView} from './RunView.js';
import {MediaContainerEvent} from './MediaContainerEvent.js';

export class View {
  constructor () {
    this.init();
  }

  init () {
    // Register AppState CHANGE listeners as early as possible so the view is the first object
    // to be notified when the AppState changes occur
    this.registerAppStateChangeListeners();

    this.view = div('root_' + uuidv4());
    this.view.classList.add('applicationRoot');
    // The gwd class is used to grant CSS styles from Google Web Designer a higher specificity
    // than standard .applicationRoot styles.
    this.view.classList.add('gwd');
    // The themesettings class is used to grant CSS styles from Studio's Theme Designer a higher
    // specificity than both standard .applicationRoot styles, AND Google Web Designer styles.
    // The themesettings class is assigned AFTER the gwd class, so it has a higher specificity.
    // For example, in the following rules, blue wins:
    //   .applicationRoot.gwd .headline_txt { color: red }
    //   .applicationRoot.themesettings .headline_txt { color: blue }
    this.view.classList.add('theme_settings');
    this.unmutedAutoplayRejected = false;

    this.runviews = [];

    // Configure tasks to perform when the boot sequence starts
    MegaScreen.globals.bootManager.on(START, e => {
      let contentTaskManager = MegaScreen.globals.bootManager.getTaskByName('ContentManager');

      // Silent boot mode: If the query-string parameter "loadscreen" is "none", then hide the
      // loading splashscreen.
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('loadscreen') === 'none') {
        document.querySelector('#splashscreen').classList.add('hidden');
      }

      // Load-progress bar for boot sequence
      let loadMediaTask = contentTaskManager.getTaskByName('LOAD_MEDIA');
      let loadThemesTask = contentTaskManager.getTaskByName('LOAD_THEMES');

      let updateBootProgress = () => {
        // Prevent the progress bar from appearing unnecessarily when loading a single theme only
        // or when loading fewer than 6 media assets. (Note that the social icons count as 6 assets,
        // so loading even a single user asset will trigger the display of the media-load bar.)
        let includeThemeProgress = MegaScreen.globals.dataStore.themes.length > 1;
        let includeMediaProgress = MegaScreen.globals.mediaCache.numAssets > 5;

        if (includeMediaProgress || includeThemeProgress) {
          document.querySelector('#splashscreen #bootProgressBar').classList.add('contentLoading');
        }

        let total = 0;
        let progress = 0;
        if (includeMediaProgress) {
          total++;
          progress += loadMediaTask.progress;
        }
        if (includeThemeProgress) {
          total++;
          progress += loadThemesTask.progress;
        }

        this.bootProgressBar.setTotal(total);
        this.bootProgressBar.setValue(progress);
      };
      loadMediaTask.on(CHANGE, updateBootProgress);
      loadThemesTask.on(CHANGE, updateBootProgress);

      // Register to be notified when themes have loaded so the main view can sync to the state of the
      // application before any other visual content is configured. The syncToState() method adds
      // CSS classes to the .applicationRoot <div> representing the app state (e.g., current product,
      // current theme, etc).
      contentTaskManager.on(TASK_END, e => {
        if (e.detail.task.name === 'LOAD_THEMES') {
          this.syncToState(MegaScreen.globals.appState.state);
        }
      });
    });

    // Register to be notified when the boot process is complete.
    MegaScreen.globals.bootManager.on(COMPLETE, e => {
      this.registerForRunViewMediaReady();
      this.registerForUnmutedVideoPlayRejected();
      this.fitToWindow();
    });

    let runviewsContainer = div('runviews');
    runviewsContainer.classList.add('visible_all_states');
    runviewsContainer.classList.add('theme_settings');
    this.rundown = new RundownComponent();
    this.rundown.setBootManager(MegaScreen.globals.bootManager);
    this.rundown.setMRTSConnection(MegaScreen.globals.mrtsConnection);
    this.rundown.setAppState(MegaScreen.globals.appState);
    let themes = div('themes');
    themes.style.display = 'none';
    this.backgroundView = new MediaView('background');
    this.backgroundView.fullscreen = true;
    this.backgroundView.playOnce = true;
    this.backgroundView.setAppState(MegaScreen.globals.appState);
    this.foregroundView = new MediaView('foreground');
    this.foregroundView.fullscreen = true;
    this.foregroundView.hiddenProducts = ['blank'];
    this.foregroundView.setAppState(MegaScreen.globals.appState);
    this.sponsorView = new MediaView('sponsor');
    this.sponsorView.setAppState(MegaScreen.globals.appState);
    this.showMediaView = new MediaView('show');
    this.showMediaView.setAppState(MegaScreen.globals.appState);
    this.ctaMediaView = new MediaView('cta');
    this.ctaMediaView.setAppState(MegaScreen.globals.appState);
    // Convenience reference to all "MediaView" layers managed by the View class. Used when an
    // operation needs to be applied to all MediaView instances, such as when AudioManager calls
    // setMuted() on all MediaView instances.
    this.mediaviews = [this.backgroundView, this.foregroundView, this.sponsorView, this.showMediaView, this.ctaMediaView];

    document.body.append(themes);
    this.view.append(this.backgroundView.view,
      runviewsContainer,
      this.rundown.view,
      this.foregroundView.view,
      this.sponsorView.view,
      this.showMediaView.view,
      this.ctaMediaView.view);
    document.body.append(this.view);

    this.bootProgressBar = new ProgressBar();
    this.bootProgressBar.view.id = 'bootProgressBar';
    document.body.querySelector('#splashscreen').insertAdjacentElement('beforeend', this.bootProgressBar.view);

    this.elementTooltip = new ElementTooltip();
    document.body.append(this.elementTooltip.view);

    this.debugPanel = new DebugPanel(MegaScreen.globals.mousetrap);
    document.body.append(this.debugPanel.view);
    this.debugPanel.setLogger(MegaScreen.globals.logger);
    this.debugPanel.hide();
    this.debugPanel.on(CLOSE, () => {
      // Notify Director that the debugger closed so it can manage application focus
      MegaScreen.globals.postMessageManager.send(PostMessages.DEBUGGER_CLOSED);
    });
    this.debugPanel.on(OPEN, () => {
      // Notify Director that the debugger opened so it can manage application focus
      MegaScreen.globals.postMessageManager.send(PostMessages.DEBUGGER_OPENED);
    });

    // Creates keyboard shortcut to toggle fullscreen mode
    MegaScreen.globals.mousetrap.bind('f', () => MegaScreen.globals.screenfull.toggle());

    // TODO: Fix crashes on iOS when the user pinches to zoom after scale is applied
    // TODO: Add "zoom controls" to disable scaling and remove the scale
    //  transform (i.e., show at 100%)
    // Scale the main application to fit within the viewport
    window.addEventListener('resize', this.fitToWindow.bind(this));
    window.addEventListener('click', this.windowClickListener.bind(this));
    window.addEventListener('keydown', this.windowKeyDownListener.bind(this));
  }

  /**
   * Triggered any time the user clicks on the application.
   */
  windowClickListener () {
    // If the browser has blocked a video from autoplaying with sound, then unmute sounds if the
    // user clicks anywhere in the application. This is the simplest user interface possible that
    // does not require displaying an "unmute" button. An unmute button cannot be displayed because
    // the application might be displaying content on air in a television broadcast, where
    // user-interface components should not be displayed.
    if (this.unmutedAutoplayRejected) {
      MegaScreen.globals.audioManager.setMuted(false);
      this.unmutedAutoplayRejected = false;
    }
  }

  /**
   * Triggered any time the user presses a key on the keyboard.
   */
  windowKeyDownListener (e) {
    if (e.key === 'm') {
      // The user pressed the keyboard shortcut to toggle "mute/unmute", which counts as user
      // interaction, so future video autoplay will succeed. Hence, the unmutedAutoplayRejected
      // flag can now be cleared.
      if (this.unmutedAutoplayRejected) {
        this.unmutedAutoplayRejected = false;
      }
    }
  }

  /**
   * Register listeners for CHANGE events from MRTSClientAppState.
   */
  registerAppStateChangeListeners () {
    MegaScreen.globals.appState.on(CHANGE, e => {
      // Handle MRTSClientAppState changes that affect the top-level view. Note that the
      // MRTSClientAppState 'state' property is intentionally not handled here directly. Instead,
      // the View waits for the current active RunView's RunView.STATE_MEDIA_READY event, and
      // handles changes to 'state' there, which prevents partial rendering of content when the
      // state changes. For details see registerForRunViewMediaReady().
      if (e.detail.prop === 'voteVisibility') {
        this.updateVoteVisibility();
      } else if (e.detail.prop === 'webpollState') {
        // The current scene is a web poll, and the web poll has changed state (e.g., to 'open',
        // 'closed', or 'draft'), so update the View to reflect the web poll's new state.
        this.syncToWebpollState();
      }

      if (e.detail.prop === 'state') {
        const currentThemeSet = MegaScreen.globals.themeService.getCurrentThemeSet();

        if (isNil(currentThemeSet)) {
          MegaScreen.globals.postMessageManager.send({
            message: PostMessages.THEMESET_NOT_FOUND,
            data: {
              themesetID: MegaScreen.globals.themeService.getCurrentThemeSetID(),
              stateName: e.detail.value,
              sceneID: get(MegaScreen.globals.appState.scene, 'id')
            }
          });
        }
      }
    });
  }

  fitToWindow () {
    const halignSetting = get(MegaScreen.globals.dataStore.deploymentSettings, 'screen.align_horizontal', 'center');
    const valignSetting = get(MegaScreen.globals.dataStore.deploymentSettings, 'screen.align_vertical', 'center');

    const scaleSetting = get(MegaScreen.globals.dataStore.deploymentSettings, 'screen.scalemode');
    const scaleToFit = isIframed() || isNil(scaleSetting) || scaleSetting === 'contain';

    // Set expected canvas width and height
    let unscaledWidth = 1920;
    let unscaledHeight = 1080;

    // Calculate and apply scaling based on available space
    let availWidth = document.documentElement.clientWidth;
    let availHeight = document.documentElement.clientHeight;
    let scale = 1; // By default, don't scale
    if (scaleToFit) {
      scale = 1/Math.max(unscaledWidth / availWidth, unscaledHeight / availHeight);
    }
    this.view.style.transform = `scale(${scale})`;
    this.view.style.transformOrigin = '0 0';

    // Apply alignment
    let totalGutterWidth = availWidth - unscaledWidth*scale;
    let totalGutterHeight = availHeight - unscaledHeight*scale;

    this.view.style.marginLeft = 0;
    switch (halignSetting) {
      case 'center':
        this.view.style.marginLeft = `${totalGutterWidth/2}px`;
        break;
      case 'right':
        this.view.style.marginLeft = `${totalGutterWidth}px`;
        break
    }

    this.view.style.marginTop = 0;
    switch (valignSetting) {
      case 'center':
        this.view.style.marginTop = `${totalGutterHeight/2}px`;
        break;
      case 'bottom':
        this.view.style.marginTop = `${totalGutterHeight}px`;
        break
    }
  }

  /**
   * Register to be notified when the currently active RunView has finished buffering the media for
   * the current state (e.g., question image), and is about to render that state (typically, a scene).
   * Within the same browser-rendering frame, the View accordingly synchronizes to the current state
   * such that all content appears on screen simultaneously, without any elements missing or
   * "popping in" too early or too late.
   *
   * TODO:
   * This implementation (RunViews dispatching STATE_MEDIA_READY) should be replaced in future with
   * a MegaScreen-wide StateRenderer that synchronizes same-frame renderering across the entire
   * application. See ticket #93:
   * https://playmegaphone.unfuddle.com/a#/projects/36870/tickets/by_number/93
   */
  registerForRunViewMediaReady () {
    this.runviews.forEach(runview => {
      runview.on(RunView.STATE_MEDIA_READY, e => {
        this.syncToState(e.detail.newState, e.detail.oldState);
      });
    });
  }

  /**
   * Listen for MediaContainerEvent.VIDEO_PLAY_REJECTED events from ALL main scene-media videos (aka
   * "question media" videos) and all MediaViews in the application (e.g., Foreground, Background).
   */
  registerForUnmutedVideoPlayRejected () {
    this.runviews.forEach(runview => {
      if (runview.valid && notNil(runview.sceneMediaContainer)) {
        runview.sceneMediaContainer.on(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED, this.unmutedVideoPlayRejectedListener.bind(this));
      }
    });
    this.mediaviews.forEach(mediaview => {
      mediaview.media.on(MediaContainerEvent.UNMUTED_VIDEO_PLAY_REJECTED, this.unmutedVideoPlayRejectedListener.bind(this));
    });
  }

  /**
   * Handle video playback rejections by the browser.
   */
  unmutedVideoPlayRejectedListener () {
    // Inform Director that an unmuted video couldn't be played. Director will tell the user
    // how to unmute the video.
    MegaScreen.globals.postMessageManager.send(PostMessages.UNMUTED_AUTOPLAY_REJECTED);

    // Mute all videos in the entire application
    MegaScreen.globals.audioManager.setMuted(true);

    // Set a flag indicating that a video-play rejection has occurred. This flag is used by
    // windowClickListener() and windowKeyDownListener() to provide a simplified user interface for
    // unmuting sound.
    this.unmutedAutoplayRejected = true;
  }

  syncToWebpollState () {
    this.updateVoteVisibility();
  }

  syncToState (newState, oldState) {
    // Add the name of the current state to the root <div>. This allows custom CSS
    // in a theme to apply styles based on the current state (e.g., to hide certain
    // elements or change the color of text during, say, the main scoreboard).
    // Replace the CSS state_* class if it has changed only (otherwise, replacement would re-trigger
    // any CSS animations defined for the state_* class).
    if (!this.view.classList.contains(newState)) {
      // State has changed, so remove the old state and add the new state
      removeClassByPrefix(this.view, 'state_');
      this.view.classList.add(newState);
    }

    let currentScene = MegaScreen.globals.appState.effectiveScene;

    // Theme name
    removeClassByPrefix(this.view, 'current_screen_theme_fieldname');
    let themeFieldName = get(MegaScreen.globals.themeService.getCurrentScreenTheme(), 'field_name', null);
    if (notNil(themeFieldName)) {
      this.view.classList.add(`current_screen_theme_fieldname_${themeFieldName}`);
    }

    // Product name
    removeClassByPrefix(this.view, 'current_screen_product');

    let screenProductName = MegaScreen.globals.appState.effectiveScreenProductName;
    if (notNil(screenProductName)) {
      this.view.classList.add(`current_screen_product_${screenProductName}`);
    }

    // Product group
    removeClassByPrefix(this.view, 'current_screen_productgroup');
    let screenProductGroup = null;
    let productService = MegaScreen.globals.productService;
    if (notNil(screenProductName)) {
      if (productService.isMultichoiceProduct(screenProductName)) {
        screenProductGroup = MULTICHOICE_PRODUCT;
      } else if (productService.isSocialProduct(screenProductName)) {
        screenProductGroup = SOCIAL_PRODUCT;
      } else if (productService.isTitleProduct(screenProductName)) {
        screenProductGroup = TITLE_PRODUCT;
      }
      if (notNil(screenProductGroup)) {
        this.view.classList.add(`current_screen_productgroup_${screenProductGroup}`);
      }
    }

    // Quiz
    this.view.classList.remove('quiz');
    let isQuiz = MegaScreen.globals.sceneService.isQuizScene(currentScene);
    if (isQuiz) {
      this.view.classList.add('quiz');
    }

    // Number of answers
    removeClassByPrefix(this.view, 'current_numanswers');
    if (notNil(currentScene)) {
      if (MegaScreen.globals.sceneService.isQuizScene(currentScene)
        || MegaScreen.globals.sceneService.isPollScene(currentScene)) {
        let numAnswers = MegaScreen.globals.sceneService.getAnswers(currentScene).length;
        this.view.classList.add(`current_numanswers_${numAnswers}`);
      }
    }

    // Vote visibility
    if (newState === STATE_SCENE || newState === STATE_SCENE_RESULT) {
      this.updateVoteVisibility();
    } else {
      this.clearVoteVisibility();
    }

    // Points visibility
    this.view.classList.remove('hide_points');
    let showPoints;
    if (newState === STATE_SCENE) {
      showPoints = MegaScreen.globals.cascadingSettingsService.getPointsVisibility(CLIENT_TYPE_SCREEN, MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore);
      if (!showPoints) {
        this.view.classList.add('hide_points');
      }
    }

    // Countdown visibility
    this.view.classList.remove('hide_countdown');
    let showCountdown;
    if (newState === STATE_SCENE || newState === STATE_SCENE_RESULT) {
      showCountdown = MegaScreen.globals.cascadingSettingsService.getCountdownVisibility(CLIENT_TYPE_SCREEN, MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore, newState);
      if (!showCountdown) {
        this.view.classList.add('hide_countdown');
      }
    }

    // Apply CSS classes for current product layout and layout characteristics. These CSS classes
    // are used to style UGC content.
    removeClassByPrefix(this.view, 'current_product_layout');
    removeClassByPrefix(this.view,'layout_');
    let productLayout = MegaScreen.globals.extensionService.cascadingGetSceneLayout(currentScene, MegaScreen.globals.dataStore.playlist);

    if (notNil(productLayout)) {
      productLayout = productLayout.toLowerCase();
      this.view.classList.add(`current_product_layout_${productLayout}`);

      if (MegaScreen.globals.layoutService.isDramatic(productLayout)) {
        this.view.classList.add('layout_dramatic');
      }

      if (MegaScreen.globals.layoutService.hasTitles(productLayout)) {
        this.view.classList.add('layout_hastitles');
      }

      if (MegaScreen.globals.layoutService.isFullscreen(productLayout)) {
        this.view.classList.add('layout_isfullscreen');
      }
    }
  }

  updateVoteVisibility () {
    this.clearVoteVisibility();

    let state;

    if (MegaScreen.globals.appState.isCurrentSceneWebPoll()) {
      if (MegaScreen.globals.appState.webpollState === STATE_CLOSED) {
        state = STATE_SCENE_RESULT;
      } else {
        state = STATE_SCENE;
      }
    } else {
      state = MegaScreen.globals.appState.state;
    }

    // "Show Votes" is enabled for the current state by a playlist setting or project setting
    let showVotesSettingEnabled = MegaScreen.globals.cascadingSettingsService.getVotesVisibility(CLIENT_TYPE_SCREEN, MegaScreen.globals.appState.scene, MegaScreen.globals.dataStore, state);

    // "Show Votes" is enabled by Director's realtime manual "Show Votes" option, which overrides
    // playlist-level and project-level settings.
    let showVotesOverrideEnabled = MegaScreen.globals.appState.voteVisibility.screen === 'visible';

    if (showVotesSettingEnabled || showVotesOverrideEnabled) {
      this.view.classList.add('show_votes');
    } else {
      this.view.classList.add('hide_votes');
    }
  }

  clearVoteVisibility () {
    this.view.classList.remove('hide_votes');
    this.view.classList.remove('show_votes');
  }
}
